<template>
  <div class="depreciation-detail-page">
    <div class="toolbar">
      <Button color="primary" @click="calculateDepreciation">计提折旧</Button>
      <Button color="red" @click="deleteLastDepreciation">删除最近折旧</Button>
      <Button @click="load">刷新</Button>
    </div>

    <!-- 统计卡片 -->
    <div class="statistics-cards" v-if="statistics">
      <div class="stat-card">
        <div class="stat-label">本月折旧</div>
        <div class="stat-value">¥{{ formatMoney(statistics.monthlyDepreciation) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">累计折旧</div>
        <div class="stat-value">¥{{ formatMoney(statistics.accumulatedDepreciation) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">计提资产数</div>
        <div class="stat-value">{{ statistics.assetCount }}项</div>
      </div>
    </div>

    <!-- 折旧明细列表 -->
    <Table :datas="list" :loading="loading">
      <TableItem title="资产编码" prop="assetCode" align="center"></TableItem>
      <TableItem title="资产名称" prop="assetName" align="left"></TableItem>
      <TableItem title="类别" prop="category" align="center"></TableItem>
      <TableItem title="原值" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.originalValue) }}
        </template>
      </TableItem>
      <TableItem title="月折旧额" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.monthlyDepreciation) }}
        </template>
      </TableItem>
      <TableItem title="累计折旧" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.accumulatedDepreciation) }}
        </template>
      </TableItem>
      <TableItem title="净值" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.netValue) }}
        </template>
      </TableItem>
      <TableItem title="已提月数" prop="depreciatedMonths" align="center"></TableItem>
      <TableItem title="剩余月数" align="center">
        <template slot-scope="{data}">
          {{ Math.max(0, data.usefulLife - data.depreciatedMonths) }}
        </template>
      </TableItem>
      <TableItem title="状态" align="center">
        <template slot-scope="{data}">
          <span :class="'status-' + data.status">{{ getStatusText(data.status) }}</span>
        </template>
      </TableItem>
    </Table>

    <!-- 空状态 -->
    <div v-if="!loading && list.length === 0" class="empty-state">
      <i class="h-icon-inbox" style="font-size: 48px; color: #ccc;"></i>
      <p>暂无折旧数据</p>
      <p class="hint">请先在"固定资产"中添加资产，然后点击"计提折旧"</p>
    </div>
  </div>
</template>

<script>
import fixedAssetApi from '../../api/fixedasset';

export default {
  name: 'DepreciationDetail',
  data() {
    return {
      list: [],
      loading: false,
      statistics: null
    };
  },
  mounted() {
    this.load();
    this.loadStatistics();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await fixedAssetApi.getFixedAssets();
        // 只显示有折旧的资产
        this.list = (res.data || []).filter(item => item.accumulatedDepreciation > 0 || item.monthlyDepreciation > 0);
      } finally {
        this.loading = false;
      }
    },
    async loadStatistics() {
      try {
        const res = await fixedAssetApi.getFixedAssetStatistics();
        this.statistics = res.data;
      } catch (e) {
        console.error('加载统计失败', e);
      }
    },
    async calculateDepreciation() {
      if (!confirm('确认计提本月折旧？')) return;
      try {
        await fixedAssetApi.calculateDepreciation();
        this.$Message.success('折旧计提完成');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '计提失败');
      }
    },
    async deleteLastDepreciation() {
      if (!confirm('确认删除当前账期最近一次折旧？此操作会同步删除对应计提折旧凭证。')) return;
      try {
        const res = await fixedAssetApi.deleteLastDepreciation();
        this.$Message.success((res.data && res.data.message) || '删除最近折旧完成');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '删除最近折旧失败');
      }
    },
    getStatusText(status) {
      const map = { 0: '在用', 1: '报废', 2: '出售', 3: '盘亏' };
      return map[status] || '未知';
    },
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
};
</script>

<style scoped>
.depreciation-detail-page {
  padding: 20px;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar button {
  margin-right: 8px;
}

.statistics-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px 24px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 15px rgba(245, 87, 108, 0.35);
}

.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.35);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.status-0 { color: #52c41a; font-weight: 500; }
.status-1 { color: #999; }
.status-2 { color: #faad14; font-weight: 500; }
.status-3 { color: #f5222d; font-weight: 500; }

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state .hint {
  font-size: 12px;
  color: #bbb;
  margin-top: 8px;
}
</style>
